import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Backdrop, Divider } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import { AppActions, CustomLink } from '../../types/decisions';
import { getEnvironmentVariable } from '../../utils';
// import headerLogo from '../../assets/images/sparkdit-logo.svg';
import { useAuth } from '../../context/auth-context';
import { UserDropdown } from '../UserDropdown/UserDropdown';
import { useSiteConfig } from '../../context/site-configs-context';
import './header.scss';

const platformURL = getEnvironmentVariable('REACT_APP_PLATFORM_URL');
const LOGIN_URL = `${platformURL}/login`;
const REGISTRATION_URL = `${platformURL}/register`;

const Header: React.FC<{
    headerLinks: CustomLink[];
    externalItems: CustomLink[];
    showDecide: boolean;
    showReset: boolean;
    shouldDisplaySteps?: boolean;
    onActionClick?: ((action: AppActions) => void) | null;
}> = ({
    headerLinks,
    externalItems,
    showDecide,
    showReset,
    onActionClick,
}) => {
    const {
        isAuthLoading,
        isAuthenticated,
        userInfo,
        logout,
        goToPlatform,
    } = useAuth();
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const navigate = useNavigate();
    const [ mobileNavActive, setMobileNavActive ] = useState(false);
    const { logo } = useSiteConfig();

    const toggleMobileNav = () => {
        setMobileNavActive(!mobileNavActive);
    };

    const onClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        action: 'DECIDE' | 'IMPORTANCE' | 'SETTINGS' | 'RESET',
    ) => {
        e.stopPropagation();
        e.preventDefault();

        onActionClick?.(action);
    };

    if (isAuthLoading) {
        return null;
    }

    const loginUrl = `${LOGIN_URL}?callback=${window.location.href}`;

    return (
        <>
            <header className="app-header">
                <div className="app-header__logo">
                    <img src={logo} alt="logo" onClick={() => navigate('/')}/>
                </div>
                <nav className={`app-header__nav ${mobileNavActive ? 'active' : ''}`}>
                    {isMobile && <Divider/>}
                    {showDecide && (
                        <Link
                            key="decide" to=""
                            onClick={(e) => onClick(e, 'DECIDE')}
                            className="app-header__link"
                        >
                            Decide
                        </Link>
                    )}
                    {showReset && (
                        <Link
                            key="reset" to=""
                            onClick={(e) => onClick(e, 'RESET')}
                            className="app-header__link"
                        >
                            Reset
                        </Link>
                    )}
                    {headerLinks.map(item => (
                        <Link
                            key={item.label}
                            to={item.link}
                            target={item.target || '_blank'}
                            className="app-header__link"
                        >
                            {item.label}
                        </Link>
                    ))}
                    {isMobile && externalItems.length > 0 && (
                        <>
                            <Divider/>
                            {externalItems.map(item => (
                                <Link
                                    key={item.label}
                                    to={item.link}
                                    target="_blank"
                                    className="app-header__link"
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </>
                    )}
                    {isMobile && <Divider/>}
                    <div className="app-header__buttons">
                        <>
                            {isAuthenticated && userInfo ? (
                                <UserDropdown
                                    username={userInfo.username}
                                    onLogout={logout}
                                    onGoToPlatform={goToPlatform}
                                />
                            ) : (
                                <>
                                    <Link
                                        className="app-header__button app-header__button--signup"
                                        to={REGISTRATION_URL}
                                    >
                                        Register
                                    </Link>
                                    <Link
                                        className="app-header__button app-header__button--login"
                                        to={loginUrl}
                                    >
                                        Login
                                    </Link>
                                </>
                            )}
                        </>
                    </div>
                </nav>
                <div className="app-header__menu-icon" onClick={toggleMobileNav}>
                    {mobileNavActive ? <>&times;</> : <>&#9776;</>}
                </div>
            </header>
            <Backdrop open={mobileNavActive} onClick={() => setMobileNavActive(false)}/>
        </>
    );
};

export default Header;
